import React from 'react';
import type { LightPoint } from '../../types/ambilight';
import { useAmbilightContext } from '../../context/AmbilightContext';

interface LightNodeProps {
  point: LightPoint;
}

export const LightNode: React.FC<LightNodeProps> = ({ point }) => {
  const { selectedPointId, selectPoint, getBulbNamesForPoint, getBulbCountForPoint } =
    useAmbilightContext();

  const isSelected = selectedPointId === point.id;
  const bulbCount = getBulbCountForPoint(point.id);
  const bulbNames = getBulbNamesForPoint(point.id);

  // Luminancia percibida para contraste óptico del texto interior
  const isBright =
    point.color.r * 0.299 + point.color.g * 0.587 + point.color.b * 0.114 > 140;

  // Posición del tooltip según la esquina o borde para no salirse de pantalla
  const getBadgePosition = (pos: string) => {
    switch (pos) {
      case 'tl':
        return 'top-full left-0 mt-2.5';
      case 'tr':
        return 'top-full right-0 mt-2.5';
      case 'bl':
        return 'bottom-full left-0 mb-2.5';
      case 'br':
        return 'bottom-full right-0 mb-2.5';
      case 'tc':
        return 'top-full left-1/2 -translate-x-1/2 mt-2.5';
      case 'bc':
        return 'bottom-full left-1/2 -translate-x-1/2 mb-2.5';
      case 'cl':
        return 'top-1/2 left-full -translate-y-1/2 ml-2.5';
      case 'cr':
        return 'top-1/2 right-full -translate-y-1/2 mr-2.5';
      default:
        return 'top-full left-1/2 -translate-x-1/2 mt-2.5';
    }
  };

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        selectPoint(point.id);
      }}
      className="group relative cursor-pointer select-none flex items-center justify-center p-1.5"
      style={{ zIndex: isSelected ? 50 : 35 }}
      title={`${point.label} — Luz: ${bulbNames}`}
    >
      {/* 
        Punto de Luz / Foco:
        Literal solo un círculo.
        Dentro del círculo: el número de focos que hay.
        Si hay 0: no muestra ningún número.
      */}
      <div
        className={`relative flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full border-2 transition-all duration-200 cursor-pointer ${
          isSelected
            ? 'scale-125 border-amber-300 ring-4 ring-amber-400/50 shadow-[0_0_28px_rgba(251,191,36,0.9)]'
            : 'border-white/80 hover:scale-115 hover:border-white shadow-[0_0_16px_rgba(0,0,0,0.8)]'
        }`}
        style={{
          backgroundColor: `rgb(${point.color.r}, ${point.color.g}, ${point.color.b})`,
          boxShadow: isSelected
            ? `0 0 25px rgba(251, 191, 36, 0.8), inset 0 1px 2px rgba(255,255,255,0.6)`
            : `0 0 16px rgba(${point.color.r}, ${point.color.g}, ${point.color.b}, 0.95), inset 0 1px 2px rgba(255,255,255,0.4)`,
        }}
      >
        {/* Número de focos que hay: Muestra 0 si no hay ningún foco asignado */}
        <span
          className="text-xs font-black font-mono leading-none select-none pointer-events-none"
          style={{
            color: isBright ? '#000000' : '#ffffff',
            textShadow: isBright ? 'none' : '0 1px 3px rgba(0,0,0,0.9)',
            opacity: bulbCount === 0 ? 0.8 : 1,
          }}
        >
          {bulbCount}
        </span>
      </div>

      {/* 
        Badge de Luz Correspondiente:
        Al hacer clic en la esquina o punto, muestra inmediatamente a qué luz corresponde
      */}
      {isSelected && (
        <div
          className={`absolute ${getBadgePosition(
            point.position
          )} z-50 pointer-events-none transition-all duration-200 animate-in fade-in zoom-in-95`}
        >
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-950/95 border border-amber-400/80 shadow-2xl shadow-black/90 backdrop-blur-xl whitespace-nowrap">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse shadow-sm shadow-amber-400" />
            <div className="flex flex-col text-left">
              <span className="text-[9px] uppercase tracking-wider text-amber-300 font-semibold font-mono">
                Luz correspondiente:
              </span>
              <span className="text-xs font-bold text-white">
                {bulbNames}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
